import { useEffect, useState } from 'react';
import api, { descargarReporte } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import { crc, fechaHora, hoy } from '../utils/format.js';
import { PageHeader, Loading, EmptyState } from '../components/UI.jsx';
import { Icon } from '../components/Icons.jsx';
import FilterBar from '../components/FilterBar.jsx';

const ORDEN_TIPOS = [
  'factura_electronica',
  'tiquete_electronico',
  'factura_exportacion',
  'proforma',
];

function inicioMes() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function BadgeHacienda({ codigo, label }) {
  if (codigo === 'aceptado') {
    return (
      <span className="badge badge-success" style={{ fontWeight: 700 }}>
        {label || 'Aceptado'}
      </span>
    );
  }
  if (codigo === 'no_aplica') {
    return (
      <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--text-muted)' }}>
        {label || 'No aplica'}
      </span>
    );
  }
  return (
    <span
      className="badge"
      style={{ background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning, #d97706)', fontWeight: 700 }}
    >
      {label || 'No Aceptado'}
    </span>
  );
}

export default function VentasFiscales() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [desde, setDesde] = useState(inicioMes());
  const [hasta, setHasta] = useState(hoy());
  const [hacienda, setHacienda] = useState(''); // '' | aceptado | no_aceptado
  const [tipoActivo, setTipoActivo] = useState('todos');

  const cargar = () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (hacienda) params.hacienda = hacienda;
    api
      .get('/ventas/dashboard-fiscal', { params })
      .then((r) => setData(r.data.data))
      .catch((err) => {
        toast.error(err.response?.data?.message || 'No se pudo cargar el tablero fiscal');
        setData(null);
      });
  };

  useEffect(cargar, [desde, hasta, hacienda]);

  const limpiar = () => {
    setDesde(inicioMes());
    setHasta(hoy());
    setHacienda('');
    setTipoActivo('todos');
  };

  const consultarEstado = async (v) => {
    try {
      const { data: res } = await api.get(`/ventas/${v.id}/fe/estado`);
      toast.success(`Hacienda: ${res.data.fe_estado || res.data.ind_estado || '—'}`);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo consultar');
    }
  };

  const reenviar = async (v) => {
    try {
      const { data: res } = await api.post(`/ventas/${v.id}/fe`);
      if (res.data.estado === 'enviado') toast.success('Reenviado a Hacienda');
      else toast.error(res.data.respuesta || res.data.estado);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.message || 'No se pudo reenviar');
    }
  };

  const descargarXml = async (v) => {
    try {
      await descargarReporte(`/ventas/${v.id}/xml`, `${v.fe_clave || v.numero}.xml`);
    } catch {
      toast.error('Sin XML');
    }
  };

  if (!data) return <Loading texto="Cargando tablero fiscal..." />;

  const resumen = data.resumen || {};
  const porTipo = data.por_tipo || {};
  const items =
    tipoActivo === 'todos'
      ? data.items || []
      : porTipo[tipoActivo]?.items || [];

  return (
    <div>
      <PageHeader
        title="Tablero de ventas fiscales"
        subtitle="Factura, tiquete, exportacion y proforma — una por una, por fecha, con estado de Hacienda"
      />

      <FilterBar
        desde={desde}
        hasta={hasta}
        onDesde={setDesde}
        onHasta={setHasta}
        onLimpiar={limpiar}
        resumen={`${resumen.total_documentos || 0} documento(s) · Aceptadas ${resumen.aceptadas || 0} · No aceptadas ${resumen.no_aceptadas || 0}`}
      >
        <div className="field" style={{ margin: 0, minWidth: 160 }}>
          <label>Estado Hacienda</label>
          <select className="select" value={hacienda} onChange={(e) => setHacienda(e.target.value)}>
            <option value="">Todos</option>
            <option value="aceptado">Aceptado</option>
            <option value="no_aceptado">No Aceptado</option>
          </select>
        </div>
      </FilterBar>

      {/* Resumen global */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
          marginBottom: 18,
        }}
      >
        <StatCard
          label="Aceptadas por Hacienda"
          value={resumen.aceptadas || 0}
          sub={crc(resumen.monto_aceptado || 0)}
          color="var(--success, #16a34a)"
        />
        <StatCard
          label="No aceptadas"
          value={resumen.no_aceptadas || 0}
          sub={crc(resumen.monto_no_aceptado || 0)}
          color="var(--warning, #d97706)"
        />
        <StatCard
          label="No aplica (proforma/ticket)"
          value={resumen.no_aplica || 0}
          sub="Sin envio a Hacienda"
          color="var(--text-muted)"
        />
        <StatCard
          label="Documentos en periodo"
          value={resumen.total_documentos || 0}
          sub={`${desde || '…'} → ${hasta || '…'}`}
          color="var(--accent-texto)"
        />
      </div>

      {/* Una columna / seccion por tipo */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 12,
          marginBottom: 20,
        }}
      >
        <TipoCard
          activa={tipoActivo === 'todos'}
          onClick={() => setTipoActivo('todos')}
          label="Todas"
          cantidad={data.items?.length || 0}
          aceptadas={resumen.aceptadas}
          noAceptadas={resumen.no_aceptadas}
        />
        {ORDEN_TIPOS.map((t) => {
          const block = porTipo[t];
          if (!block) return null;
          return (
            <TipoCard
              key={t}
              activa={tipoActivo === t}
              onClick={() => setTipoActivo(t)}
              label={block.label}
              cantidad={block.cantidad}
              aceptadas={block.aceptadas}
              noAceptadas={block.no_aceptadas}
              monto={block.monto}
            />
          );
        })}
      </div>

      {/* Lista detalle una por una */}
      <h3 style={{ fontSize: 16, marginBottom: 12 }}>
        Detalle {tipoActivo === 'todos' ? 'de todas las ventas' : `— ${porTipo[tipoActivo]?.label || ''}`}
      </h3>

      {items.length === 0 ? (
        <EmptyState
          icon="ventas"
          titulo="Sin documentos en este filtro"
          texto="Cambie el rango de fechas o el estado de Hacienda."
        />
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Numero</th>
                <th>Tipo</th>
                <th>Cliente</th>
                <th className="text-right">Total</th>
                <th>Hacienda</th>
                <th>Detalle FE</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((v) => (
                <tr key={v.id}>
                  <td className="muted" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                    {fechaHora(v.fecha)}
                  </td>
                  <td style={{ fontWeight: 600 }}>{v.numero}</td>
                  <td>
                    <span
                      className="badge"
                      style={{ background: 'var(--accent-soft)', color: 'var(--accent-texto)', fontSize: 11 }}
                    >
                      {porTipo[v.tipo_comprobante]?.label || v.tipo_comprobante}
                    </span>
                  </td>
                  <td>{v.cliente_nombre || <span className="muted">Contado</span>}</td>
                  <td className="text-right mono" style={{ fontWeight: 600 }}>
                    {crc(v.total)}
                  </td>
                  <td>
                    <BadgeHacienda codigo={v.hacienda_codigo} label={v.hacienda_label} />
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {v.fe_estado || '—'}
                    {v.fe_clave ? (
                      <div className="mono" style={{ fontSize: 10, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {v.fe_clave}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div className="flex gap-sm" style={{ justifyContent: 'flex-end' }}>
                      {v.fe_xml || v.fe_clave ? (
                        <>
                          <button className="btn btn-ghost btn-sm" title="XML" onClick={() => descargarXml(v)}>
                            <Icon.download width={15} height={15} />
                          </button>
                          <button className="btn btn-ghost btn-sm" title="Consultar Hacienda" onClick={() => consultarEstado(v)}>
                            Estado
                          </button>
                          {['generado', 'error', 'rechazado', 'enviado'].includes(v.fe_estado) && (
                            <button className="btn btn-ghost btn-sm" title="Reenviar" onClick={() => reenviar(v)}>
                              Reenviar
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 700, color, fontFamily: 'var(--font-display)' }}>{value}</div>
      {sub && (
        <div className="muted mono" style={{ fontSize: 13, marginTop: 4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function TipoCard({ label, cantidad, aceptadas, noAceptadas, monto, activa, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card"
      style={{
        padding: 14,
        textAlign: 'left',
        cursor: 'pointer',
        border: activa ? '2px solid var(--accent-vivo)' : '1px solid var(--border)',
        background: activa ? 'var(--accent-soft)' : 'var(--surface)',
      }}
    >
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{cantidad}</div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {aceptadas != null && (
          <>
            <span style={{ color: 'var(--success, #16a34a)' }}>{aceptadas} acept.</span>
            {' · '}
            <span style={{ color: 'var(--warning, #d97706)' }}>{noAceptadas || 0} no acept.</span>
          </>
        )}
      </div>
      {monto != null && (
        <div className="mono" style={{ fontSize: 13, marginTop: 4, fontWeight: 600 }}>
          {crc(monto)}
        </div>
      )}
    </button>
  );
}
